import { type ReactNode } from "react";
import { Heart, Shield, Star, Zap, CheckCircle2 } from "lucide-react";
import { Breadcrumbs, type Crumb } from "@/components/Breadcrumbs";
import { MeetChrisVideoButton } from "@/components/MeetChrisVideoButton";

export type HeroImage = {
  /** Imported asset URL */
  src: string;
  /** Auto-generated descriptive alt text — keyword + location. */
  alt: string;
};

export function PageHero({
  eyebrow,
  title,
  description,
  children,
  image,
  breadcrumbs,
  h1Text,
  belowTitle,
  hideTrustSignals = false,
  hideMeetChris = false,
  hideTrustList = false,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Optional hero photo rendered behind the gradient overlay. */
  image?: HeroImage;
  /** Optional breadcrumb trail rendered above the eyebrow for SEO + UX. */
  breadcrumbs?: Crumb[];
  /** When set, this becomes the literal <h1> (rendered small above the big visual title)
   *  and the visual `title` is demoted to an <h2>. Use to put an exact keyword in H1
   *  without sacrificing the marketing headline. */
  h1Text?: string;
  /** Optional node rendered just under the title (e.g. LastUpdated stamp). */
  belowTitle?: ReactNode;
  /** Opt out of the default Meet Chris button + trust signals block. */
  hideTrustSignals?: boolean;
  /** Hide only the Meet Chris pill while keeping the rest of the trust signals. */
  hideMeetChris?: boolean;
  /** Hide only the bottom icon trust list while keeping stars + Meet Chris. */
  hideTrustList?: boolean;
}) {
  return (
    <section className="relative overflow-hidden bg-gradient-dark text-brand-cream">
      {image && (
        <img
          src={image.src}
          alt={image.alt}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
      )}
      <div className="absolute inset-0 bg-gradient-radial-gold opacity-70" />
      {image && (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-brand-black/70 via-brand-black/50 to-brand-black/80"
        />
      )}
      <div className="relative mx-auto max-w-5xl px-5 py-24 text-center lg:px-8 lg:py-32">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="mb-6 flex justify-center">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}
        {eyebrow && (
          <p className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
            {eyebrow}
          </p>
        )}
        {h1Text && (
          <h1 className="mt-6 text-sm font-bold uppercase tracking-[0.25em] text-brand-gold">
            {h1Text}
          </h1>
        )}
        {h1Text ? (
          <h2 className="mt-3 text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {title}
          </h2>
        ) : (
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {title}
          </h1>
        )}
        {belowTitle && <div className="mt-4 flex justify-center">{belowTitle}</div>}
        {description && (
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-brand-cream/80">
            {description}
          </p>
        )}
        {children && <div className="mt-8">{children}</div>}
        {!hideTrustSignals && (
          <div className="mt-8 flex flex-col items-center gap-5 text-brand-cream/85">
            {!hideMeetChris && <MeetChrisVideoButton variant="light" />}
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-0.5 text-brand-gold">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="font-semibold">150+ 5-Star Reviews across Colorado</span>
            </div>
            {!hideTrustList && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-1.5">
                <Heart className="h-4 w-4 text-brand-gold" />
                Talk To The Owner Directly
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-brand-gold" />
                Colorado-Only Agency
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Zap className="h-4 w-4 text-brand-gold" />
                25+ Years of Experience
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-brand-gold" />
                No Salespeople. No Outsourcing.
              </span>
            </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
